import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

const series = [
  {
    letter: 'A',
    name: 'A Series',
    tagline: 'Smart modular office spaces',
    description: 'Apple-cabin-style modular spaces designed for productivity, work, and commercial applications. From compact solo pods to executive suites.',
    image: '/images/6a50655936e4.webp',
    link: '/a-range-details',
    quoteLink: '/contact?model=A%20Series',
  },
  {
    letter: 'X',
    name: 'X Series',
    tagline: 'Premium living capsules',
    description: 'Redefining modern living with flagship prefabricated architecture. Panoramic views, luxury finishes, and uncompromising build quality.',
    image: '/images/65c0b0130fa8.webp',
    link: '/x-range-details',
    quoteLink: '/contact?model=X%20Series',
  },
  {
    letter: 'M',
    name: 'M Series',
    tagline: 'Modular adventure cabins for remote and hard-to-access locations',
    description: 'Revolutionary detachable-module design reaches where cranes cannot. Piece-by-piece assembly brings luxury to the most isolated destinations.',
    image: '/images/4f4d4413e7ef.webp',
    link: '/m-range-details',
    quoteLink: '/contact?model=M%20Series',
  },
];

export default function CoreProductRanges() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section className="relative py-24 md:py-32 px-4 md:px-8 bg-gradient-to-b from-[#000000] via-[#080c14] to-[#000000]">
      {/* Subtle grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(0,217,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,217,255,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }}></div>

      <div className="max-w-7xl mx-auto relative z-10" ref={sectionRef}>
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="text-xs text-cyan-400 uppercase tracking-widest mb-4">CORE PRODUCT RANGES</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-wide text-white">
            Explore Our Core Series
          </h2>
          <p className="text-gray-400 text-base md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
            Three distinct product lines engineered for different environments — from urban workspaces to the most remote wilderness
          </p>
        </div>

        {/* Three Equal Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {series.map((item, index) => (
            <div
              key={item.letter}
              className={`group relative bg-gradient-to-br from-[#14141e]/90 to-[#0a0a0f]/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg overflow-hidden hover:border-cyan-400/60 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,217,255,0.15)] hover:-translate-y-2 cursor-pointer flex flex-col transition-all ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Image Area */}
              <div className="relative w-full h-56 md:h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={`${item.name} modular pod`}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-600"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent"></div>

                {/* Letter Badge */}
                <div className="absolute top-4 left-4 w-10 h-10 md:w-12 md:h-12 bg-cyan-500 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,217,255,0.4)]">
                  <span className="text-lg md:text-xl font-bold text-black">{item.letter}</span>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6 md:p-8 flex flex-col flex-1">
                <h3 className="text-xl md:text-2xl font-medium text-white mb-2">{item.name}</h3>
                <p className="text-cyan-400 text-sm md:text-base font-medium mb-3 leading-snug">{item.tagline}</p>
                <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">{item.description}</p>

                <Link
                  to={item.link}
                  className="group/btn inline-flex items-center justify-between w-full px-5 py-3 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium hover:bg-cyan-500 hover:text-black hover:border-cyan-500 transition-all hover:shadow-[0_0_25px_rgba(0,217,255,0.4)] whitespace-nowrap cursor-pointer"
                >
                  <span>Explore Series</span>
                  <div className="w-7 h-7 bg-cyan-500/20 group-hover/btn:bg-black/20 rounded-full flex items-center justify-center transition-all">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>

                <Link
                  to={item.quoteLink}
                  className="mt-3 inline-flex items-center justify-between w-full px-5 py-2.5 border border-white/10 rounded-full text-gray-400 text-sm font-medium hover:text-cyan-400 hover:border-cyan-400/50 transition-all whitespace-nowrap cursor-pointer"
                >
                  <span>Request Quote</span>
                  <div className="w-6 h-6 bg-white/5 rounded-full flex items-center justify-center transition-all">
                    <i className="ri-price-tag-3-line text-xs"></i>
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Z Series Coming Soon Link */}
        <div className="text-center">
          <Link
            to="/z-range-details"
            className="inline-flex items-center gap-3 px-4 py-2 text-gray-500 hover:text-cyan-400 transition-colors cursor-pointer group text-sm"
          >
            <div className="w-5 h-5 bg-gray-700/50 rounded-full flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
              <span className="text-[10px] font-bold text-gray-400 group-hover:text-cyan-400">Z</span>
            </div>
            <span>Z Series</span>
            <span className="text-xs text-gray-600 group-hover:text-cyan-500/70 transition-colors">— Coming Soon</span>
            <i className="ri-arrow-right-line text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
